import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';

const CheckoutPaymentDisplay = () => {
    const [selectedMethod, setSelectedMethod] = useState('');
    const [paymentDisplay, setPaymentDisplay] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showFallback, setShowFallback] = useState(false);

    const mountedRef = useRef(false);
    const activeRequest = useRef(null);
    const displayCache = useRef(new Map());
    const lastLoadTime = useRef({});
    const fallbackTimer = useRef(null);

    // Constants for better maintainability
    const cacheSize = 10;
    const contactEmail = 'Contact us';
    const fallbackTimeout = 20000; // 20 seconds in milliseconds

    // Memoize checkout data to prevent recomputation on every render
    const checkoutData = useMemo(() => {
        const blocksData = window.walletUpBlockData || {};
        const classicData = window.walletUpCheckoutData || {};
        
        return {
            ajaxUrl: blocksData.ajaxUrl || classicData.ajaxUrl || '',
            nonces: blocksData.nonces || classicData.nonces || {},
            amount: blocksData.amount || classicData.amount || '0',
            methods: blocksData.methods || classicData.methods || {},
            settings: blocksData.wcQRSettings || classicData.wcQRSettings || {}
        };
    }, []);

    const { ajaxUrl, nonces, amount, methods, settings } = checkoutData;

    // Use WooCommerce specific QR settings
    const qrDisplaySettings = useMemo(() => {
        const wcQRSettings = settings.wcQRSettings || {};
        return {
            size: wcQRSettings.size || 'medium',
            color: wcQRSettings.color || '#674FBF',
            background: wcQRSettings.background || '#F4F4FF',
            border: wcQRSettings.border ?? true,
            style: wcQRSettings.style || 'rounded',
            checkout_display: wcQRSettings.checkout_display || 'below_payment',
            show_on_confirmation: wcQRSettings.show_on_confirmation ?? true,
            show_default_message: wcQRSettings.show_default_message ?? true,
            custom_message: wcQRSettings.custom_message || '',
            hide_message: wcQRSettings.hide_message ?? false
        };
    }, [settings]);

    // Safe nonce access
    const paymentNonce = useMemo(() => (
        nonces?.wcPaymentDisplay || 
        document.querySelector('input[name="wallet_up_payment_nonce"]')?.value || 
        ''
    ), [nonces]);

    // Initialization
    useEffect(() => {
        mountedRef.current = true;
        const initialMethod = document.getElementById('wallet_up_payment_method')?.value || Object.keys(methods)[0];

        if (initialMethod) {
            setSelectedMethod(initialMethod);
            loadPaymentDisplay(initialMethod); // Load initial method
        }

        // Cart update listener
        const handleCartUpdate = debounce(() => {
            if (selectedMethod) {
                // Clear cache only if amount changes
                if (checkoutData.amount !== amount) {
                    displayCache.current.clear();
                }
                loadPaymentDisplay(selectedMethod);
            }
        }, 500);

        document.body.addEventListener('updated_cart_totals', handleCartUpdate);

        // Set up fallback message timer
        fallbackTimer.current = setTimeout(() => {
            if (mountedRef.current && !paymentDisplay) {
                setShowFallback(true);
            }
        }, fallbackTimeout);

        return () => {
            mountedRef.current = false;
            if (activeRequest.current) activeRequest.current.abort();
            document.body.removeEventListener('updated_cart_totals', handleCartUpdate);
            clearTimeout(fallbackTimer.current); // Clear the timeout on unmount
        };
    }, [methods, amount]);

    // Instant method selection without debounce
    const handleMethodSelect = useCallback((method) => {
        setSelectedMethod(method);
        const cacheKey = `${method}_${amount}`;
        if (displayCache.current.has(cacheKey)) {
            setPaymentDisplay(displayCache.current.get(cacheKey));
            setShowFallback(false); // Reset fallback if a payment method loads
        } else {
            loadPaymentDisplay(method); // Load if not in cache
        }
    }, [amount]);

    const loadPaymentDisplay = useCallback(async (method) => {
        if (!method || !mountedRef.current || !ajaxUrl || !paymentNonce) return;

        try {
            setLoading(true);
            setError('');
            setShowFallback(false); // Reset fallback if loading starts

            if (activeRequest.current) {
                activeRequest.current.abort();
            }

            const formData = new FormData();
            formData.append('action', 'wallet_up_wc_payment_display');
            formData.append('method', method);
            formData.append('amount', amount);
            formData.append('nonce', paymentNonce);
            formData.append('timestamp', Date.now());
            formData.append('qr_settings', JSON.stringify(qrDisplaySettings));

            activeRequest.current = new AbortController();
            const response = await fetch(ajaxUrl, {
                method: 'POST',
                body: formData,
                credentials: 'same-origin',
                signal: activeRequest.current.signal
            });

            if (!mountedRef.current) return;

            const data = await response.json();

            if (!data.success) {
                throw new Error(data.data?.message || 'Failed to load payment display');
            }

            const cacheKey = `${method}_${amount}`;
            if (displayCache.current.size >= cacheSize) {
                const oldestKey = [...displayCache.current.keys()].sort((a, b) => lastLoadTime.current[a] - lastLoadTime.current[b])[0];
                displayCache.current.delete(oldestKey);
            }
            displayCache.current.set(cacheKey, data.data.html);
            lastLoadTime.current[cacheKey] = Date.now();
            setPaymentDisplay(data.data.html);

            // Update WooCommerce select
            const wcSelect = document.getElementById('wallet_up_payment_method');
            if (wcSelect) {
                wcSelect.value = method;
                wcSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }

        } catch (err) {
            if (err.name !== 'AbortError' && mountedRef.current) {
                setError(err.message || 'An unexpected error occurred');
                console.error('Payment display error:', err);
            }
        } finally {
            if (mountedRef.current) {
                setLoading(false);
                activeRequest.current = null;
            }
        }
    }, [amount, ajaxUrl, paymentNonce, qrDisplaySettings]);

    // Render optimization
    const methodItems = useMemo(() => {
        return Object.entries(methods).map(([id, method]) => (
            <div
                key={id}
                onClick={() => handleMethodSelect(id)}
                className={`wallet-up-method-item ${selectedMethod === id ? 'selected' : ''}`}
                data-method={id}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        handleMethodSelect(id);
                    }
                }}
                aria-label={`Select ${method.name} payment method`}
            >
                <img 
                    src={method.icon} 
                    alt={method.name} 
                    className="wallet-up-method-icon"
                    loading="lazy"
                />
                <span className="wallet-up-method-name">{method.name}</span>
            </div>
        ));
    }, [methods, selectedMethod, handleMethodSelect]);

    const SkeletonLoader = () => (
        <div className="skeleton-wrapper">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="skeleton-item"></div>
            ))}
        </div>
    );

    return (
        <div className="wallet-up-modern-wrapper">
            {loading ? (
                <SkeletonLoader />
            ) : error ? (
                <div className="woocommerce-error" role="alert">
                    {error}
                    <button onClick={() => loadPaymentDisplay(selectedMethod)}>Retry</button>
                </div>
            ) : showFallback && !paymentDisplay ? (
                <div className="wallet-up-no-methods">
                    <h3>Payment Loading</h3>
                    <p>There seems to be an issue loading payment options. Please try again or contact support for assistance.</p>
                    <a href={`mailto:${contactEmail}`}>Contact Support</a>
                </div>
            ) : (
                <>
                    <div className="wallet-up-methods-container">
                        {methodItems}
                    </div>

                    <div 
                        id="wallet-up-payment-display" 
                        className={`wallet-up-payment-display ${loading ? 'is-loading' : ''} display-mode-${qrDisplaySettings.style}`}
                        style={{
                            '--qr-color': qrDisplaySettings.color,
                            '--qr-background': qrDisplaySettings.background,
                            '--qr-border': qrDisplaySettings.border ? '2px solid var(--qr-color)' : 'none',
                            'display': 'flex', // Ensure flex display for centering
                            'justifyContent': 'center', // Center horizontally
                            'alignItems': 'center', // Center vertically
                            'minHeight': '200px' // Ensure a minimum height for consistency
                        }}
                    >
                        <div 
                            dangerouslySetInnerHTML={{ __html: paymentDisplay }}
                            className="payment-content"
                        />
                    </div>
                </>
            )}
            {/* Inline CSS for simplicity, consider moving to a CSS file in a real project */}
            <style jsx>{`
                .wallet-up-modern-wrapper {
                    margin: 20px 0;
                    padding: 20px;
                    background: #ffffff;
                    border-radius: 16px;
                }

                .wallet-up-methods-container {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
                    gap: 15px;
                    margin-bottom: 20px;
                }

                .wallet-up-method-item {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding: 15px;
                    border-radius: 12px;
                    background: #ffffff;
                    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                    cursor: pointer;
                    transition: all 0.3s ease;
                    border: 2px solid transparent;
                }

                .wallet-up-method-item:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
                }

                .wallet-up-method-item.selected {
                    border-color: #674FBF;
                    background: #F4F4FF;
                }

                .wallet-up-method-icon {
                    width: 48px;
                    height: 48px;
                    object-fit: contain;
                    margin-bottom: 8px;
                }

                .wallet-up-method-name {
                    font-size: 14px;
                    font-weight: 500;
                    color: #333;
                    text-align: center;
                }

                .wallet-up-payment-display {
                    padding: 20px;
                    margin-top: 20px;
                    border-radius: 12px;
                    background: #F4F4FF;
                }

                .skeleton-wrapper {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
                    gap: 15px;
                    margin-bottom: 20px;
                }

                .skeleton-item {
                    height: 100px;
                    background: #f2f2f2;
                    border-radius: 12px;
                    animation: pulse 1.5s ease-in-out infinite;
                }

                @keyframes pulse {
                    0% {
                        background-color: rgba(165, 165, 165, 0.1);
                    }
                    50% {
                        background-color: rgba(165, 165, 165, 0.3);
                    }
                    100% {
                        background-color: rgba(165, 165, 165, 0.1);
                    }
                }

                .woocommerce-error {
                    border-radius: 8px;
                    padding: 12px;
                    margin: 10px 0;
                    background: #fff2f2;
                    color: #d63031;
                    border-left: 4px solid #d63031;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .woocommerce-error button {
                    margin-left: 10px;
                    background-color: #d63031;
                    color: white;
                    border: none;
                    padding: 5px 10px;
                    border-radius: 4px;
                    cursor: pointer;
                }

                .wallet-up-no-methods {
                    margin: 20px 0;
                    padding: 20px;
                    background: #f8d7da;
                    border-radius: 10px;
                    text-align: center;
                }

                .wallet-up-no-methods h3 {
                    color: #721c24;
                    margin-bottom: 10px;
                }

                .wallet-up-no-methods p {
                    color: #721c24;
                }

                .wallet-up-no-methods a {
                    color: #007bff;
                    text-decoration: none;
                }

                .wallet-up-no-methods a:hover {
                    text-decoration: underline;
                }
            `}</style>
        </div>
    );
};

export default CheckoutPaymentDisplay;